import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Grid,
} from "@mui/material";
import {
  FilterList as FilterIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://printformee.com/api";

const InhouseSales = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [selectedCategory, setSelectedCategory] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [salesData, setSalesData] = useState([]);
  const [categories, setCategories] = useState([
    { value: "all", label: "All" },
  ]); // Initialize with 'all' option

  // Fallback data for when API calls fail
  const fallbackCategories = [
    { value: "photo-frames", label: "Photo Frames" },
    { value: "cushions", label: "Cushions" },
    { value: "rock-slate", label: "Rock Slate" },
    { value: "wall-art", label: "Wall Art" },
    { value: "decorative-items", label: "Decorative Items" },
  ];

  const fallbackSalesData = [
    { id: 1, productName: "Vintage Photo Frame", totalSale: 145 },
    { id: 2, productName: "Modern Cushion Set", totalSale: 89 },
    { id: 3, productName: "Rock Slate Art", totalSale: 67 },
    { id: 4, productName: "Canvas Wall Art", totalSale: 234 },
    { id: 5, productName: "Decorative Vase", totalSale: 112 },
    { id: 6, productName: "Wooden Frame", totalSale: 178 },
    { id: 7, productName: "Silk Cushion", totalSale: 93 },
    { id: 8, productName: "Stone Slate", totalSale: 45 },
    { id: 9, productName: "Abstract Art Print", totalSale: 156 },
    { id: 10, productName: "Ceramic Decor", totalSale: 78 },
  ];

  // API functions to fetch real data
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/inhouse-sales/categories`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setCategories([{ value: "all", label: "All" }, ...response.data]);
    } catch (error) {
      console.error("Error fetching categories:", error);
      // Use fallback data silently
      setCategories([{ value: "all", label: "All" }, ...fallbackCategories]);
    }
  };

  const fetchSalesData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/inhouse-sales?page=${page}&limit=10&category=${selectedCategory}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setSalesData(response.data.data || response.data);
      setTotalPages(response.data.totalPages || 5);
    } catch (error) {
      console.error("Error fetching sales data:", error);
      // Use fallback data silently (no error message to user)
      setSalesData(fallbackSalesData);
      setTotalPages(5);
    } finally {
      setLoading(false);
    }
  }, [page, selectedCategory]);

  // Load data when component mounts or when page/category changes
  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchSalesData();
  }, [fetchSalesData]);

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const handleFilter = () => {
    console.log("Filter applied with category:", selectedCategory);
    // Reset to page 1 when filter changes
    setPage(1);
    // Manually trigger data reload
    fetchSalesData();
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    console.log("Page changed to:", newPage);
    // In real app, this would trigger API call with new page
  };

  // Calculate serial number based on current page and index
  const getSerialNumber = (index) => {
    return (page - 1) * 10 + index + 1;
  };

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: "#f7f8fc",
        minHeight: "100vh",
      }}
    >
      {/* Page Title */}
      <Box
        sx={{
          display: "flex",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 3,
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 1 : 2,
        }}
      >
        <TrendingUpIcon
          sx={{
            mr: isMobile ? 0 : 2,
            fontSize: isMobile ? 24 : 32,
            color: "#0d47a1",
          }}
        />
        <Typography
          variant={isMobile ? "h5" : "h4"}
          sx={{
            fontWeight: 600,
            color: "#1a1a1a",
            textAlign: isMobile ? "center" : "left",
          }}
        >
          Inhouse Sale
        </Typography>
      </Box>

      {/* Main Card Container */}
      <Card
        sx={{
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
          borderRadius: 3,
          backgroundColor: "white",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Filter Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: isMobile ? "stretch" : "center",
              mb: 3,
              pb: 2,
              borderBottom: "1px solid #e0e0e0",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 2 : 0,
            }}
          >
            <FormControl
              size="medium"
              sx={{
                minWidth: isMobile ? "100%" : 200,
                flex: isMobile ? 1 : "auto",
              }}
            >
              <Select
                value={selectedCategory}
                onChange={handleCategoryChange}
                displayEmpty
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: "white",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    "&:hover": {
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                    },
                  },
                }}
              >
                {categories.map((category, index) => (
                  <MenuItem
                    key={`${category.value}-${index}`}
                    value={category.value}
                  >
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={<FilterIcon />}
              onClick={handleFilter}
              sx={{
                backgroundColor: "#0d47a1",
                borderRadius: 2,
                px: isMobile ? 2 : 4,
                py: 1.5,
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "0 4px 8px rgba(13, 71, 161, 0.3)",
                "&:hover": {
                  backgroundColor: "#1565c0",
                  boxShadow: "0 6px 12px rgba(13, 71, 161, 0.4)",
                },
              }}
            >
              Filter
            </Button>
          </Box>

          {/* Sales Data Table */}
          <Box sx={{ mb: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 300,
                }}
              >
                <CircularProgress />
              </Box>
            ) : isMobile ? (
              // Mobile Card View
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {salesData.map((row, index) => (
                  <Card key={row.id} sx={{ p: 2, border: "1px solid #e0e0e0" }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        #{getSerialNumber(index)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.date}
                      </Typography>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{ mb: 1, fontSize: "0.875rem", fontWeight: 500 }}
                    >
                      {row.productName}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Total Sale:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#0d47a1",
                          fontSize: "0.875rem",
                        }}
                      >
                        Rs {Number(row.totalSale || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Card>
                ))}
              </Box>
            ) : (
              // Desktop Table View
              <TableContainer
                component={Paper}
                sx={{
                  boxShadow: "none",
                  borderRadius: 2,
                  overflow: "hidden",
                  overflowX: "auto",
                }}
              >
                <Table sx={{ minWidth: 600 }}>
                  <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#2c3e50",
                          border: "none",
                          py: 2,
                          minWidth: 80,
                        }}
                      >
                        SL
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#2c3e50",
                          border: "none",
                          py: 2,
                          minWidth: 200,
                        }}
                      >
                        Product Name
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 600,
                          color: "#2c3e50",
                          border: "none",
                          py: 2,
                          minWidth: 150,
                        }}
                      >
                        Total Sale
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesData.map((row, index) => (
                      <TableRow
                        key={row.id}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "white" : "#f8f9fa",
                          "&:hover": {
                            backgroundColor: "#e3f2fd",
                            cursor: "pointer",
                          },
                          transition: "background-color 0.2s ease",
                        }}
                      >
                        <TableCell sx={{ border: "none", py: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {getSerialNumber(index)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ border: "none", py: 2 }}>
                          <Typography variant="body2" sx={{ color: "#2c3e50" }}>
                            {row.productName}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            border: "none",
                            py: 2,
                            fontWeight: 600,
                            color: "#0d47a1",
                          }}
                        >
                          {row.totalSale}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          {/* Pagination */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              shape="rounded"
              size="large"
              sx={{
                "& .MuiPaginationItem-root": {
                  borderRadius: 2,
                  fontWeight: 500,
                },
                "& .MuiPaginationItem-page.Mui-selected": {
                  backgroundColor: "#0d47a1",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#1565c0",
                  },
                },
                "& .MuiPaginationItem-page": {
                  color: "#546e7a",
                  backgroundColor: "white",
                  border: "1px solid #e0e0e0",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    borderColor: "#0d47a1",
                  },
                },
                "& .MuiPaginationItem-previousNext": {
                  backgroundColor: "white",
                  border: "1px solid #e0e0e0",
                  color: "#546e7a",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    borderColor: "#0d47a1",
                  },
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default InhouseSales;
